<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Form Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <b-form @submit.prevent="submitForm">
      <b-row class="mt-4">
        <b-col>
          <b-alert variant="info" show>
            <i class="fa fa-info-circle" />
            Analytes are applied to the sample based on the sample's index within the submission. Submission samples are indexed based on the the test package they are initially created from.
            'Individual' test packages do not contain indexed samples and will apply all analytes to all samples regardless of index.
            <br><br>
            Example: Sample/Depth #2 from an SF11 soil submission. SF14 test package is applied to the sample. Only analytes from the SF14 sample #2 definition will be applied.
          </b-alert>

          <h3>Sample Type</h3>
          <div>
            <CustomFormRadioGroup
              :lg="4"
              :md="4"
              :options="sample_type_options"
              v-model="form_data.selected_sample_type"
            />
          </div>

          <h3>Test Package</h3>
          <div v-if="form_data.selected_sample_type">
            <div v-if="loading_test_package" class="text-center mt-5 font-2xl text-muted">
              <i class="fal fa-spinner fa-spin" /> Test Package Loading...
            </div>

            <CustomFormRadioGroup
              v-else
              :lg="4"
              :md="4"
              :grouped="true"
              :options="test_package_options"
              v-model="form_data.selected_test_package"
            />
          </div>
          <b-alert v-else variant="danger" show>
            <i class="fa fa-info-circle" />
            Sample Type Selection Required
          </b-alert>

          <h3>Submission</h3>
          <b-form-group v-if="form_data.selected_sample_type">
            <multiselect
              v-model="form_data.selected_submissions"
              :options="submission_options"
              :multiple="true"
              :allow-empty="false"
              track-by="id"
              label="name"
              placeholder="Search Submissions"
              :loading="loading_submission"
              open-direction="bottom"
              :internal-search="false"
              :disabled="!form_data.selected_test_package"
              @search-change="debounceFetchSubmission"
            />
          </b-form-group>
          <b-alert v-else variant="danger" show>
            <i class="fa fa-info-circle" />
            Sample Type Selection Required
          </b-alert>
        </b-col>
      </b-row>

      <!-- validation error -->
      <b-row v-if="invalid" class="mt-4">
        <b-col>
          <b-alert variant="danger" show>
            <i class="fa fa-exclamation-triangle" /> Please review the form and complete all required fields.
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-button
            type="submit"
            class="my-4 float-right py-3 px-5"
            variant="success"
          >
            <i class="fal fa-save" /> Save
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import { getCollection } from '@/shared/fetch-utils'
import Multiselect from 'vue-multiselect'
import { required } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import CustomFormRadioGroup from '@/components/shared/form/CustomFormRadioGroup.vue'

export default {
  name: 'TestPackageApplyTestPackageForm',
  components: {
    Multiselect,
    CustomFormRadioGroup
  },
  mixins: [validationMixin],
  props: {
  },
  data () {
    return {
      loading: true,

      form_data: {
        selected_sample_type: null,
        selected_test_package: null,
        selected_submissions: []
      },

      sample_type_collection: null,
      sample_type_options: [],
      loading_test_package: false,

      test_package_collection: null,
      test_package_options: [],

      submission_collection: null,
      submission_options: [],
      loading_submission: false
    }
  },
  validations: {
    form_data: {
      selected_sample_type: { required },
      selected_test_package: { required },
      selected_submissions: { required }
    }
  },
  computed: {
    invalid () {
      return this.$v && this.$v.$dirty && this.$v.$invalid
    }
  },
  watch: {
    'form_data.selected_sample_type' () {
      this.form_data.selected_test_package = null
      this.form_data.selected_submissions = []
      this.fetchTestPackages()
    },
    'form_data.selected_test_package' () {
      this.form_data.selected_submissions = []
      this.fetchSubmissions()
    }
  },
  created () {
    this.loading = true

    this.debounceFetchSubmission = _.debounce((query) => this.fetchSubmissions(query), 300)

    this.init()
  },
  methods: {
    submitForm () {
      this.$v.$touch()
      if (this.invalid) return

      return this.$emit('submit', this.form_data)
    },

    async fetchSampleTypes () {
      this.sample_type_collection = await getCollection({
        collection_type: 'sample_type',
        message: this.$bvToast,
        limit: "all"
      })

      this.sample_type_options = this.sample_type_collection.models.map((sample_type) => {
        return {
          text: sample_type.name,
          value: sample_type.id,
          icon: 'cube'
        }
      })
    },

    async fetchTestPackages () {
      this.loading_test_package = true

      const filters = [
        {
          logic: 'filterEquals',
          field: 'quality_control',
          value: '0'
        }
      ]

      if (this.form_data.selected_sample_type) {
        filters.push({
          logic: 'filterEquals',
          field: 'sample_type_id',
          value: this.form_data.selected_sample_type
        })
      }

      this.test_package_collection = await getCollection({
        collection_type: 'test_package',
        message: this.$bvToast,
        connections: [
          'test_package_group'
        ],
        filters,
        limit: "all"
      })

      // unique test package groups by id
      const test_package_groups = _.uniqBy(this.test_package_collection.models.map(({ test_package_group }) => test_package_group), 'id')

      this.test_package_options = test_package_groups.filter((test_package_group) => test_package_group).map((test_package_group) => {
        return {
          text: test_package_group.name,
          options: this.test_package_collection.models.filter(({ test_package_group_id }) => test_package_group_id === test_package_group.id).map((test_package) => {
            return {
              text: test_package.name,
              value: test_package.id,
              icon: 'cube'
            }
          })
        }
      })

      this.loading_test_package = false
    },

    async fetchSubmissions (query) {
      this.loading_submission = true

      const filters = [
      ]

      if (this.form_data.selected_sample_type) {
        filters.push({
          logic: 'filterEquals',
          field: 'sample_type_id',
          value: this.form_data.selected_sample_type
        })
      }

      if (query) {
        filters.push({
          logic: "filterEquals",
          field: "pid",
          value: query
        })
      }

      this.submission_collection = await getCollection({
        collection_type: 'submission',
        message: this.$bvToast,
        filters
      })

      this.submission_options = this.submission_collection.models.map(({ id, pid, project }) => {
        return {
          id,
          name: `${pid} | ${
            project.customer ? project.customer.full_name : "Customer not specified"
          }`
        }
      })

      this.loading_submission = false
    },

    async init () {
      await this.fetchSampleTypes()

      this.loading = false
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
